import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { usePhaserKeyboardSuspend } from '@/hooks/usePhaserKeyboardSuspend';

export interface SearchableSelectOption {
  id: string;
  label: string;
  /** A second line under the label — a count, an address, whatever tells two alike apart. */
  hint?: string;
}

/** A row under the matches that is not one of them, e.g. "New organization…". */
export interface SearchableSelectAction {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  /** The selected option's id; its label is what the closed field reads. */
  value?: string | null;
  /**
   * What the closed field reads when `value` is not one of `options` — a
   * current choice that is not itself on offer, such as the organization an
   * island is already in, which is never one of the places it can move to.
   */
  valueLabel?: string;
  onSelect: (id: string) => void;
  action?: SearchableSelectAction;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Shown in place of the list when the typed term matches nothing. */
  emptyText?: string;
  className?: string;
}

/** The option's label with the matched run marked, so the eye lands on the reason it is there. */
function Highlighted({ text, term }: { text: string; term: string }) {
  const at = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (at < 0) {
    return <>{text}</>;
  }

  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-game-golden/30 text-white rounded-sm px-0.5">
        {text.slice(at, at + term.length)}
      </mark>
      {text.slice(at + term.length)}
    </>
  );
}

/**
 * A select you can type into: the field filters its options as you go and the
 * list is walked with the arrow keys.
 *
 * It exists because the lists it is pointed at are unbounded — every
 * organization on the platform, for one — and a plain dropdown of them is a
 * scroll, not a choice. The filtering is client-side on purpose: the caller has
 * already loaded the options it is willing to offer, and narrowing them is not
 * a question worth a request.
 *
 * Nothing is selected by typing alone. Enter (or a click) takes the highlighted
 * row, Escape abandons the search and puts the current selection back — so a
 * half-typed term never becomes a choice the caller then has to undo.
 */
export function SearchableSelect({
  options,
  value,
  valueLabel,
  onSelect,
  action,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  disabled = false,
  emptyText = 'No matches',
  className = ''
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const phaserKeyboard = usePhaserKeyboardSuspend();

  const selectedLabel = useMemo(() => {
    const option = options.find(entry => entry.id === value);
    return option?.label ?? (value ? valueLabel ?? null : null);
  }, [options, value, valueLabel]);

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option => option.label.toLowerCase().includes(needle));
  }, [options, term]);

  /** The rows the keyboard walks: the matches, and the action row after them. */
  const entryCount = matches.length + (action ? 1 : 0);

  // Options can change under an open list — a reload, or a term that narrows it
  // — and an index left pointing past the end would select nothing on Enter.
  useEffect(() => {
    setActiveIndex(current => Math.min(current, Math.max(entryCount - 1, 0)));
  }, [entryCount]);

  const close = useCallback(() => {
    setIsOpen(false);
    setTerm('');
    setActiveIndex(0);
  }, []);

  // A click anywhere else is an abandoned search, not a selection.
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  const choose = (index: number) => {
    if (action && index === matches.length) {
      close();
      // A click moves focus to the row that is about to disappear; the field it
      // came from is where the next keystroke belongs.
      inputRef.current?.focus();
      action.onSelect();
      return;
    }

    const option = matches[index];
    if (!option) return;

    close();
    inputRef.current?.focus();
    onSelect(option.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(0);
        return;
      }
      if (entryCount === 0) return;
      setActiveIndex(current => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return Math.min(Math.max(next, 0), entryCount - 1);
      });
      return;
    }

    if (event.key === 'Enter') {
      // Never the enclosing form's Enter: this one is picking a row.
      event.preventDefault();
      if (isOpen) {
        choose(activeIndex);
      } else {
        setIsOpen(true);
      }
      return;
    }

    if (event.key === 'Escape') {
      if (isOpen) {
        // Stop the keypress before it reaches a modal that would close on it.
        event.preventDefault();
        event.stopPropagation();
        close();
      }
      return;
    }

    if (event.key === 'Tab' && isOpen) {
      close();
    }
  };

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  // Walking the list with the arrow keys has to move the list, not just the
  // highlight: past the fold the active row would otherwise be off screen.
  useEffect(() => {
    if (!isOpen) return;
    const active = containerRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`);
    (active as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
    // `optionId` is derived from `listboxId`, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeIndex, entryCount, listboxId]);

  return (
    <div className={`relative ${className}`.trim()} ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={isOpen && entryCount > 0 ? optionId(activeIndex) : undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          value={isOpen ? term : selectedLabel ?? ''}
          placeholder={selectedLabel ?? placeholder}
          onFocus={event => {
            phaserKeyboard.onFocus();
            // Focus alone does not open the list: a selection hands the focus
            // back to this field, and reopening on the way would undo it.
            // Clicking, typing and the arrow keys all open it.
            event.target.select();
          }}
          onBlur={phaserKeyboard.onBlur}
          onClick={() => !disabled && setIsOpen(true)}
          onChange={event => {
            setTerm(event.target.value);
            setActiveIndex(0);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="w-full flex items-center pl-3 pr-9 py-2 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:outline-none transition-all duration-200 disabled:opacity-60"
        />
        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600 pointer-events-none" />
      </div>

      {isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-game-dark border border-gray-700 p-1 z-[2002] rounded-lg shadow-xl"
        >
          {matches.length === 0 && !action ? (
            <p className="px-3 py-2 text-sm text-gray-400 italic m-0">{emptyText}</p>
          ) : (
            <>
              {matches.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400 italic m-0">{emptyText}</p>
              )}
              {matches.map((option, index) => (
                <button
                  key={option.id}
                  id={optionId(index)}
                  type="button"
                  role="option"
                  aria-selected={option.id === value}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(index)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors duration-150 ${
                    index === activeIndex ? 'bg-game-medium/60' : 'hover:bg-game-medium/40'
                  }`}
                >
                  <span className="block text-gray-100 text-sm truncate">
                    <Highlighted text={option.label} term={term.trim()} />
                  </span>
                  {option.hint && (
                    <span className="block text-gray-400 text-xs truncate">{option.hint}</span>
                  )}
                </button>
              ))}

              {action && (
                <button
                  id={optionId(matches.length)}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseEnter={() => setActiveIndex(matches.length)}
                  onClick={() => choose(matches.length)}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 border-t border-game-medium/30 mt-1 pt-2 transition-colors duration-150 ${
                    matches.length === activeIndex ? 'bg-game-medium/60' : 'hover:bg-game-medium/40'
                  }`}
                >
                  {action.icon && <span className="text-game-golden text-xs">{action.icon}</span>}
                  <span className="text-gray-100 text-sm">{action.label}</span>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
